<script setup lang="ts">
import { DialogContent, DialogOverlay, DialogPortal, DialogTitle, VisuallyHidden } from 'reka-ui'

const { t } = useI18n()
const { enabled: isDocueAgentEnabled } = useFeatureEnabled('docue-agent')
const { activeWorkspaceId, rootFolderId, workspace } = useAuth()
const isAgentVisible = computed(() =>
  !!rootFolderId.value && (!workspace.value?.attributes.partnerId || isDocueAgentEnabled.value),
)

const isMobile = useBreakpointHelper().smaller('md')
const isOpen = useSessionStorage('docue-agent-chat-open', false)
const hasUnreadAnswer = useSessionStorage(
  () => `docue-agent-chat-unread:${activeWorkspaceId.value ?? ''}`,
  false,
)
const { events, isBusy } = useDocueAgent()
const askChat = useTemplateRef<{ focusDraftInput: () => void }>('askChat')
const sheetStyle = useVisualViewportStyle(() => isMobile.value && isOpen.value)
const hasActiveChat = computed(() => isDocueAgentEnabled.value && (isBusy.value || events.value.length > 0))

function handleOpenAutoFocus(event: Event) {
  event.preventDefault()
  if (isDocueAgentEnabled.value)
    nextTick(() => askChat.value?.focusDraftInput())
}

const completedAnswerCount = computed(() =>
  events.value.filter(event =>
    event.type === 'content.completed'
    && event.data.kind === 'text'
    && event.data.phase === 'reply'
    && typeof event.data.value === 'string'
    && !!event.data.value.trim(),
  ).length,
)

watch(completedAnswerCount, (count, previousCount) => {
  if (count > previousCount && !isOpen.value)
    hasUnreadAnswer.value = true
})

watch(isOpen, (open) => {
  if (open)
    hasUnreadAnswer.value = false
}, { immediate: true })
</script>

<template>
  <footer
    class="flex h-10 shrink-0 items-center justify-between gap-1 border-t border-gray-200 bg-white px-3"
  >
    <AppSupportButton />

    <!-- Below md the chat takes over the screen, so it needs a modal dialog rather than
    a popover anchored to a footer that the keyboard pushes out of view. The sheet is
    composed from bare reka parts because the styled UiDialogContent centers itself with
    translate transforms, and iOS Safari mispositions transformed fixed elements around
    the on-screen keyboard. -->
    <UiDialog
      v-if="isAgentVisible && isMobile"
      v-model:open="isOpen"
    >
      <UiDialogTrigger as-child>
        <AppDocueAgentTrigger
          :active="hasActiveChat"
          :busy="isDocueAgentEnabled && isBusy"
          :open="isOpen"
          :unread="isDocueAgentEnabled && hasUnreadAnswer"
        />
      </UiDialogTrigger>

      <DialogPortal>
        <!-- The sheet is sized to the visual viewport, which stops a little short of the
        keyboard, so hide the page behind with a backdrop spanning the full screen. -->
        <DialogOverlay class="fixed inset-x-0 top-0 z-50 h-lvh bg-white" />
        <DialogContent
          class="
            fixed inset-x-0 top-0 z-50 h-dvh overflow-hidden bg-white duration-200
            data-[state=open]:animate-in data-[state=open]:fade-in-0
          "
          :aria-describedby="undefined"
          :style="sheetStyle"
        >
          <VisuallyHidden>
            <DialogTitle>{{ t('docueAgent.title') }}</DialogTitle>
          </VisuallyHidden>
          <AppDocueAgentChat
            v-if="isDocueAgentEnabled"
            full-screen
            @close="isOpen = false"
          />
          <AppDocueAgentPrivateBeta
            v-else
            full-screen
            @close="isOpen = false"
          />
        </DialogContent>
      </DialogPortal>
    </UiDialog>

    <UiPopover
      v-else-if="isAgentVisible"
      v-model:open="isOpen"
    >
      <UiPopoverTrigger as-child>
        <AppDocueAgentTrigger
          :active="hasActiveChat"
          :busy="isDocueAgentEnabled && isBusy"
          :open="isOpen"
          :unread="isDocueAgentEnabled && hasUnreadAnswer"
        />
      </UiPopoverTrigger>

      <UiPopoverContent
        align="end"
        side="top"
        :side-offset="3"
        class="w-auto border-0 bg-transparent p-0 shadow-none ring-0"
        @interact-outside="(event) => event.preventDefault()"
        @open-auto-focus="handleOpenAutoFocus"
      >
        <template #default="{ close }">
          <AppDocueAgentChat
            v-if="isDocueAgentEnabled"
            ref="askChat"
            @close="close"
          />
          <AppDocueAgentPrivateBeta
            v-else
            @close="close"
          />
        </template>
      </UiPopoverContent>
    </UiPopover>
  </footer>
</template>
